import { Component, OnInit, OnDestroy } from '@angular/core';
import { filter, takeUntil, distinctUntilChanged } from 'rxjs';
import { Subject } from 'rxjs';
import { UserInfo, LogError } from '@memberjunction/core';
import { DashboardMetrics, SummaryCard } from 'src/app/utils/interfaces';
import { QueryNames } from 'src/app/utils/constants';
import { DataFetchService } from 'src/app/services/data-fetch.service';
import { ActiveUserService } from 'src/app/services/active-user.service';
import { UserAuthService } from 'src/app/services/user-auth.service';

@Component({
  selector: 'acs-dashboard',
  templateUrl: './dashboard.html',
  styleUrls: ['./dashboard.scss']
})
export class DashboardComponent implements OnInit, OnDestroy {
  private destroy$ = new Subject<void>();

  isLoading = true;
  error: string | null = null;
  
  summary!: DashboardMetrics;

  summaryCards: SummaryCard[] = [];
  currentUser!: UserInfo;
  private lastUserId: string | null = null;
  
  constructor(
    private dataFetchService: DataFetchService,
    private activeUserService: ActiveUserService,
    private userAuthService: UserAuthService
  ) {}

  ngOnInit(): void {
    // Subscribe to active user changes but only reinitialize when the actual user changes
    this.activeUserService.activeUser$.pipe(
      filter(userInfo => userInfo !== null),
      distinctUntilChanged((prev, curr) => prev?.activeUser?.ID === curr?.activeUser?.ID),
      takeUntil(this.destroy$)
    ).subscribe((userInfo) => {
      const newUserId = userInfo!.activeUser.ID;
      // Only reinitialize if the user actually changed
      if (this.lastUserId !== newUserId) {
        this.lastUserId = newUserId;
        this.initializeDashboard();
      }
    });
  }

  initializeDashboard(): void {
    this.currentUser = this.activeUserService.getActiveUser()!;
    this.loadDashboardData();
  }

  private async loadDashboardData(): Promise<void> {
    this.isLoading = true;
    this.error = null;

    try {
      await this.loadSummaryData();
    } catch (error) {
      this.error = 'Failed to load dashboard data. Please try again.';
      LogError('Dashboard load error:', undefined, error);
    } finally {
      this.isLoading = false;
    }
  }

  private async loadSummaryData(): Promise<void> {
    // Initialize summary data - will be updated when data loads
    if(this.userAuthService.isStaff()) {
      await this.getDashboardMetricsForStaff();
    }
    else {
      await this.getDashboardMetricsForChapterAdmins();
    }

    this.updateSummaryCards();
  }

  private async getDashboardMetricsForStaff(): Promise<void> {
    const result = await this.dataFetchService.executeQuery<DashboardMetrics>({
      QueryName: QueryNames.DashboardMetricsSummaryForStaff,
    });
    if(result.success && result.data.length > 0) {
      this.summary = result.data[0];
    }
    else {
      this.summary = {
        Email: this.currentUser.Email,
        TotalChapters: 0,
        TotalActiveMembers: 0,
        TotalFellows: 0
      }
    }
  }

  private async getDashboardMetricsForChapterAdmins(): Promise<void> {
    const result = await this.dataFetchService.executeQuery<DashboardMetrics>({
      QueryName: QueryNames.DashboardMetricsSummary,
      Parameters: {
        Email: this.currentUser.Email
      }
    });
    if(result.success && result.data.length > 0) {
      this.summary = result.data[0];
    }
    else {
      this.summary = {
        Email: this.currentUser.Email,
        TotalChapters: 0,
        TotalActiveMembers: 0,
        TotalFellows: 0
      }
    }
  }
  
  private updateSummaryCards(): void {
    this.summaryCards = [
      {
        id: 'total-chapters',
        value: `${this.summary.TotalChapters}`,
        label: this.summary.TotalChapters === 1 ? 'Chapter' : 'Chapters',
        sublabel: 'Total in directory',
        icon: '🏥',
        variant: 'primary',
        format: 'number'
      },
      {
        id: 'active-members',
        value: `${this.summary.TotalActiveMembers}`,
        label: 'Chapter Members',
        sublabel: 'Across all chapters',
        icon: '👥',
        variant: 'success',
        format: 'number'
      },
      {
        id: 'fellows',
        value: `${this.summary.TotalFellows}`,
        label: 'Fellows',
        sublabel: 'Total Fellows',
        icon: '👥',
        variant: 'info',
        format: 'number'
      }
    ];
  }

  get userRole(): string | null {
    // Get role from UserAuthService which fetches from API
    return this.userAuthService.getUserRole();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }
}